'use client';

import { createBrowserClient } from '@supabase/ssr';
import { getActiveController } from '@/lib/auth/controller-registry';
import { createRefreshGate } from '@/lib/auth/refresh-gate';
import { createSupabaseFetch } from '@/lib/auth/supabase-fetch';
import { expireCookie } from '@/lib/auth/token-cookies';
import { getSupabasePublicKey } from '@/lib/supabase/public-key';

const PFX = 'sb_';

const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;
    const k = '__sb_test__';
    document.cookie = `${k}=1; Path=/; SameSite=None; Secure; Partitioned`;
    cache = document.cookie.includes(k);
    // AUTH-002: the probe was written Partitioned, so only a Partitioned delete removes it.
    expireCookie(k);
    return cache;
  };
})();

const fromCookies = () =>
  typeof document === 'undefined' ? [] :
  document.cookie.split(';').filter(Boolean).map((c) => {
    const [name, ...rest] = c.trim().split('=');
    return { name: name.trim(), value: decodeURIComponent(rest.join('=')) };
  }).filter((c) => c.name);

const fromStorage = () => {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k.slice(PFX.length), value: localStorage.getItem(k) || '' }));
  } catch { return []; }
};

const setCookie = (name: string, value: string, options?: any) => {
  let s = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; SameSite=None; Secure; Partitioned`;
  if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
  if (options?.domain) s += `; Domain=${options.domain}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires).toUTCString()}`;
  document.cookie = s;
};

let clientInstance: ReturnType<typeof createBrowserClient> | null = null;

/**
 * The browser Supabase client (a singleton; @supabase/ssr also caches its own).
 *
 * AUTH-002 — session handling, as built:
 *   - Token refresh is ON. @supabase/ssr 0.10 forces `autoRefreshToken: isBrowser()`
 *     whatever is passed, so the `false` this file carried since April did nothing; it is
 *     now stated as it behaves. getSession() also refreshes on demand within 90 s of expiry.
 *   - Cross-tab coordination uses the library's default Navigator lock. The old no-op
 *     lock (aea02f6, April) only silenced a "Lock broken … steal" AbortError from an older
 *     auth-js; 2.100 handles orphaned locks itself. With the real lock two tabs never
 *     spend the same refresh token: the second reads the first one's new session from the
 *     shared cookie inside the lock.
 *   - Deleting a cookie expires every variant, Partitioned included (see token-cookies.ts).
 *     Without that the session cookie survived every sign-out.
 *   - All requests go through createSupabaseFetch: 429 back-off on refresh, idle guard
 *     and one refresh-and-resend on a 401 for data requests.
 */
export function createClient() {
  if (clientInstance) return clientInstance;

  // SEC-006 P2: the publishable key when the build has one, else the legacy anon key.
  const anonKey = getSupabasePublicKey();
  const gate = createRefreshGate(Date.now);

  clientInstance = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    anonKey,
    {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: false,
      },
      global: {
        fetch: createSupabaseFetch({
          baseFetch: (...args) => fetch(...args),
          gate,
          controller: getActiveController,
          anonKey,
        }),
      },
      cookies: {
        getAll: () => canUseCookies() ? fromCookies() : fromStorage(),
        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;
          if (canUseCookies()) {
            cookiesToSet.forEach(({ name, value, options }) =>
              value ? setCookie(name, value, options) : expireCookie(name)
            );
          } else {
            cookiesToSet.forEach(({ name, value, options }) => {
              try {
                value ? localStorage.setItem(`${PFX}${name}`, value)
                      : localStorage.removeItem(`${PFX}${name}`);
              } catch {}
              if (value) setCookie(name, value, options);
              else expireCookie(name);
            });
          }
        },
      },
    }
  );

  return clientInstance;
}
